import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {AccordionSection} from "@khanacademy/wonder-blocks-accordion";
import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import {Spring, Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingXSmall,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    markMinorConversions,
    useMarkConversion,
    type MinorConversionData,
} from "@khan/analytics-learning-time";
import {staticUrl} from "@khan/urls";

import NewBadge from "../new-badge.tsx";
import type {DomainLink, DomainObject} from "../types.ts";

/**
 * A link to a subject within a domain.
 *
 * This is rendered in the mobile menu.
 */
function DomainNavSectionLink({
    domainSlug,
    link,
    onClick,
    style,
}: {
    domainSlug: string;
    link: DomainLink;
    onClick: () => void;
    style?: StyleType;
}): React.ReactElement {
    const markConversion = useMarkConversion();

    return (
        <Link
            href={link.href}
            onClick={onClick}
            skipClientNav={link.nonContentLink}
            style={style}
            safeWithNav={() =>
                markConversion([
                    {
                        id: "global_nav_choose_subject",
                        extra: {
                            domain: domainSlug,
                            subject: link.slug,
                            view: "mobile",
                        },
                    },
                ])
            }
        >
            <LabelMedium>{link.translatedTitle}</LabelMedium>
        </Link>
    );
}

type Props = {
    domain: DomainObject;
    animated?: boolean;
    onClickLink: () => void;
};

// Exported for testing
export const getSelectDomainConversions = (
    domain: DomainObject,
): Array<MinorConversionData> => {
    return [
        {
            id: "global_nav_choose_domain",
            extra: {
                domain: domain.slug,
                view: "mobile",
            },
        },
    ];
};

class DomainNavSection extends React.Component<Props> {
    handleClick: (open: boolean) => void = (open: boolean) => {
        const {domain} = this.props;

        if (!domain.children.length) {
            // Clicking this domain will navigate to the domain page.
            // The bigBingoLinks handlers in render() will mark the
            // conversions for us, so do nothing here.
            return;
        }

        // Clicking this domain will toggle its children to appear.
        if (open) {
            // Mark the conversions if it was just opened.
            markMinorConversions(getSelectDomainConversions(domain));
        }
    };

    renderLinks: (
        links: Array<DomainLink>,
        domainSlug: string,
        onClickLink: () => void,
    ) => React.ReactNode = (
        links: Array<DomainLink>,
        domainSlug: string,
        onClickLink: () => void,
    ) => {
        const lastLinkIndex = links.length - 1;

        return (
            <ul
                className={css(styles.subjects)}
                data-test-mobile-menu-domain={domainSlug}
                key={domainSlug}
            >
                {links.map((link, i) => (
                    <li key={link.slug}>
                        <DomainNavSectionLink
                            domainSlug={domainSlug}
                            link={link}
                            onClick={onClickLink}
                            style={[
                                styles.subjectLink,
                                i === 0 && styles.subjectLinkFirst,
                                i === lastLinkIndex && styles.subjectLinkLast,
                            ]}
                        />
                    </li>
                ))}
            </ul>
        );
    };

    render(): React.ReactNode {
        const {domain, animated, onClickLink} = this.props;
        const hasChildren = domain.children.length > 0;
        // Check whether the href is for the Get Ready for Grade Level curation
        // so that we can apply specific styles
        const isGRFGLCuration = domain.href === "/math/get-ready-courses";

        const headerContent = (
            <>
                {domain.translatedTitle}

                {domain.isNew && (
                    <React.Fragment>
                        <Strut size={spacing.xxxSmall_4} />
                        <NewBadge />
                    </React.Fragment>
                )}
                <Spring />
            </>
        );

        if (!hasChildren) {
            // When there are no children, we want to mark a conversion
            // when the navigation happens.
            return (
                <View style={styles.noChildrenDomainLinkWrapper}>
                    <Link
                        href={domain.href}
                        safeWithNav={() =>
                            markMinorConversions(
                                getSelectDomainConversions(domain),
                            )
                        }
                        style={styles.noChildrenDomainLink}
                    >
                        <HeadingXSmall style={styles.domainTitle}>
                            {headerContent}
                        </HeadingXSmall>
                    </Link>
                </View>
            );
        }

        return (
            <AccordionSection
                animated={animated}
                caretPosition="start"
                cornerKind="square"
                header={
                    <HeadingXSmall
                        // Removing the heading tag from HeadingXSmall
                        // since the AccordionSection's header button
                        // is already wrapped in a heading tag.
                        tag="span"
                        style={styles.domainTitle}
                    >
                        {headerContent}
                    </HeadingXSmall>
                }
                headerStyle={isGRFGLCuration ? styles.getReadyCurationBg : null}
                onToggle={this.handleClick}
                style={styles.accordionSectionStyle}
                tag="h4"
                testId="mobile-menu-domain"
            >
                <>
                    {this.renderLinks(
                        domain.children,
                        domain.slug,
                        onClickLink,
                    )}
                </>
            </AccordionSection>
        );
    }
}

const BORDER_STYLE = `1px solid ${color.offBlack16}`;

const styles = StyleSheet.create({
    accordionSectionStyle: {
        // Override the default border of AccordionSection to avoid
        // doubled up borders for adjacent sections.
        // Note: The bottom border is added in responsive-nav-menu.tsx
        // after the last DomainNavSection.
        border: "none",
        borderTop: BORDER_STYLE,
    },
    domainTitle: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        fontSize: 14,
        padding: spacing.medium_16,
    },
    getReadyCurationBg: {
        backgroundImage: `url("${staticUrl(
            "/images/learn-menu/mobile-background.png",
        )}")`,
        backgroundPosition: "top right",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
    },
    subjects: {
        borderTop: BORDER_STYLE,
    },
    subjectLink: {
        display: "block",
        color: color.blue,
        padding: `${spacing.small_12}px ${spacing.xLarge_32}px`,
    },
    subjectLinkFirst: {
        paddingTop: spacing.medium_16,
    },
    subjectLinkLast: {
        paddingBottom: spacing.medium_16,
    },

    // Style the domain link when there are no children to look
    // like the accordion section headers for consistency.
    noChildrenDomainLinkWrapper: {
        backgroundColor: color.white,
        borderTop: BORDER_STYLE,
    },
    noChildrenDomainLink: {
        color: color.offBlack,
        // Exact space needed to line up the link text with the
        // accordion section header titles.
        paddingLeft: spacing.xLarge_32,

        ":hover": {
            color: color.blue,
        },
    },
});

export default DomainNavSection;
